/**
 * Search, ordering and grouping for long single-select pickers.
 *
 * Pulled out of the components so the behaviour is testable without a DOM, and so every
 * picker listing the same entity orders and ranks it identically. A dropdown that
 * silently truncates, or returns rows in table order, reads to the user as "the thing I
 * want is missing" — this module is what stops that.
 */

export interface SelectOption {
  /** Stable id submitted by the form. */
  value: string;
  /** Primary text — what the list is ordered by *within* a group. */
  label: string;
  /**
   * The group this option belongs to, e.g. the Account a Solution sits on. It is the
   * primary sort key, the heading options are gathered under, and — since a group heading
   * is not visible once the picker is closed — part of what the trigger shows. Options
   * with no group sort ahead of the grouped ones and render without a heading.
   */
  group?: string | null;
}

/** One heading and the options under it, in display order. */
export interface OptionGroup<T extends SelectOption = SelectOption> {
  /** null for options that carry no group. */
  group: string | null;
  options: T[];
}

/**
 * `numeric` so "Phase 2" precedes "Phase 10"; `sensitivity: "base"` so case and accents do
 * not split otherwise-adjacent names.
 */
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/**
 * Group first, then label — so the list reads Account by Account, alphabetically, with
 * each Account's own options alphabetical underneath.
 */
export function compareOptions(a: SelectOption, b: SelectOption): number {
  const byGroup = COLLATOR.compare(a.group ?? "", b.group ?? "");
  if (byGroup !== 0) return byGroup;
  return COLLATOR.compare(a.label, b.label);
}

export function sortOptions(options: readonly SelectOption[]): SelectOption[] {
  return [...options].sort(compareOptions);
}

/**
 * Every whitespace-separated token must appear in the label or the group, so "retool phat"
 * finds "Retool Support" under "Phathom Pharmaceuticals" without the user having to
 * remember which field holds which word, or type them in order.
 */
export function matchesQuery(option: SelectOption, query: string): boolean {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = `${option.label} ${option.group ?? ""}`.toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}

/**
 * The flat list a picker renders and arrows through: ordered, and never capped. Any cap
 * here is the bug we are fixing — if the query matches 400 Solutions the user gets all
 * 400 and scrolls.
 */
export function filterOptions(options: readonly SelectOption[], query: string): SelectOption[] {
  return sortOptions(options.filter((option) => matchesQuery(option, query)));
}

/**
 * The same list, gathered under headings for display. Takes an already-sorted list and
 * walks it, so the groups come out in the same order as the flat list and a caller can
 * keep using flat indices for keyboard navigation.
 */
export function groupOptions<T extends SelectOption>(sorted: readonly T[]): OptionGroup<T>[] {
  const groups: OptionGroup<T>[] = [];
  for (const option of sorted) {
    const group = option.group ?? null;
    const current = groups.at(-1);
    if (current !== undefined && current.group === group) {
      current.options.push(option);
    } else {
      groups.push({ group, options: [option] });
    }
  }
  return groups;
}
